import { useState } from 'react';
import { swapPairs } from '../data';

const maxSwap = Math.max(...swapPairs.map((p) => Math.max(Math.abs(p.long), Math.abs(p.short))));

const pipValues: Record<string, number> = {
  EURUSD: 10,
  GBPJPY: 7.5,
  USDTRY: 8,
  XAUUSD: 10,
  AUDUSD: 10,
};

const filters = [
  { key: 'All', label: 'All Pairs' },
  { key: 'Major', label: 'Majors' },
  { key: 'Cross', label: 'Crosses' },
  { key: 'Exotic', label: 'Exotics' },
  { key: 'Commodity', label: 'Commodities' },
] as const;

export default function SwapBasicsSection() {
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const filteredPairs =
    activeFilter === 'All'
      ? swapPairs
      : swapPairs.filter((p) => p.label === activeFilter);

  const toggleExpand = (pair: string) => {
    setExpandedRow((prev) => (prev === pair ? null : pair));
  };

  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Section header */}
        <div className="max-w-[640px] mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Not all swaps are created equal
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            From 0.12 pips/day to 28.50 pips/day — the pair you trade determines how expensive holding overnight really is. Below: real swap rates, scaled so you can see the gap at a glance.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1">
          {filters.map((f) => {
            const isActive = activeFilter === f.key;
            const count = f.key === 'All' ? swapPairs.length : swapPairs.filter((p) => p.label === f.key).length;
            return (
              <button
                key={f.key}
                onClick={() => {
                  setActiveFilter(f.key);
                  setExpandedRow(null);
                }}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-[13px] font-semibold cursor-pointer transition-all duration-200 ${
                  isActive
                    ? 'bg-foreground-950 text-background-50 shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 text-[11px] ${isActive ? 'text-background-50/70' : 'text-foreground-400'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Dark unified card */}
        <div className="relative bg-foreground-950 rounded-card overflow-hidden">
          <div className="absolute top-0 right-0 w-[350px] h-[350px] bg-secondary-500/5 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-accent-500/4 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative p-6 md:p-10">
            {/* Column headers */}
            <div className="hidden md:grid grid-cols-[1fr_1.5fr_0.8fr] gap-6 mb-5 pb-4 border-b border-foreground-700/40">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-foreground-400">Pair</span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-foreground-400">
                Long vs Short swap (pips/day)
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-foreground-400 text-right">
                Weekly cost at 1 lot
              </span>
            </div>

            {/* Pair rows */}
            <div className="divide-y divide-foreground-700/40">
              {filteredPairs.map((pair) => {
                const isExtreme = Math.abs(pair.long) > 10 || Math.abs(pair.short) > 10;
                const pipVal = pipValues[pair.pair] ?? 10;
                const isExpanded = expandedRow === pair.pair;

                const longBarPct = (Math.abs(pair.long) / maxSwap) * 100;
                const shortBarPct = (Math.abs(pair.short) / maxSwap) * 100;

                const longIsPositive = pair.long > 0;
                const shortIsPositive = pair.short > 0;

                const weeklyLongCost = pair.long * pipVal * 7;
                const weeklyShortCost = pair.short * pipVal * 7;

                const dominantCost = Math.max(Math.abs(weeklyLongCost), Math.abs(weeklyShortCost));
                const dominantLabel =
                  Math.abs(weeklyLongCost) >= Math.abs(weeklyShortCost)
                    ? `Long pays $${Math.abs(weeklyLongCost).toFixed(0)}`
                    : `Short pays $${Math.abs(weeklyShortCost).toFixed(0)}`;

                const costColor =
                  weeklyLongCost <= 0 && weeklyShortCost <= 0
                    ? 'text-[#F87171]'
                    : weeklyLongCost >= 0 && weeklyShortCost >= 0
                    ? 'text-secondary-300'
                    : 'text-foreground-200';

                return (
                  <div
                    key={pair.pair}
                    onClick={() => toggleExpand(pair.pair)}
                    className={`group py-6 md:py-7 first:pt-2 last:pb-2 cursor-pointer transition-all duration-200 ${
                      isExtreme ? 'relative' : ''
                    } ${
                      isExpanded
                        ? 'bg-foreground-900/60 -mx-2 px-2 rounded-lg'
                        : 'hover:bg-foreground-900/50 -mx-2 px-2 rounded-lg'
                    }`}
                  >
                    {isExtreme && !isExpanded && (
                      <div className="absolute inset-0 bg-accent-500/[0.04] rounded-lg -mx-2 -my-1 pointer-events-none" />
                    )}

                    <div className="relative grid grid-cols-1 md:grid-cols-[1fr_1.5fr_0.8fr] gap-4 md:gap-6 items-start">
                      {/* Column 1: Pair info */}
                      <div>
                        <div className="flex items-center gap-2.5 mb-1.5">
                          <span className="text-[17px] font-[750] text-background-50 tracking-tight group-hover:text-white transition-colors">
                            {pair.pair}
                          </span>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                              pair.label === 'Exotic'
                                ? 'bg-accent-500/15 text-accent-300'
                                : pair.label === 'Commodity'
                                ? 'bg-primary-500/12 text-primary-300'
                                : 'bg-secondary-500/15 text-secondary-300'
                            }`}
                          >
                            {pair.label}
                          </span>
                          {isExtreme && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent-500/15 text-accent-300 border border-accent-500/25">
                              <span className="w-1 h-1 rounded-full bg-accent-300 animate-pulse" />
                              Extreme
                            </span>
                          )}
                          {/* Expand chevron */}
                          <span
                            className={`ml-auto md:hidden text-foreground-400 transition-transform duration-200 ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                          >
                            <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center" />
                          </span>
                        </div>
                        <p className="text-[13px] leading-relaxed text-foreground-400 line-clamp-2 group-hover:text-foreground-300 transition-colors">
                          {pair.description}
                        </p>

                        {/* Mobile dollar cost */}
                        <div className="md:hidden mt-3 flex items-center gap-2 text-[13px]">
                          <span className="text-foreground-300 font-semibold">Weekly at 1 lot:</span>
                          <span className={costColor}>{dominantLabel}</span>
                        </div>
                      </div>

                      {/* Column 2: Swap bars */}
                      <div className="space-y-3.5">
                        {/* Long bar */}
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-foreground-400 w-[38px] flex-shrink-0 text-right">
                            Long
                          </span>
                          <div className="flex-1 h-7 bg-foreground-800/80 rounded-sm overflow-hidden flex items-center relative">
                            <div
                              className={`h-full flex items-center transition-all duration-500 ${
                                longIsPositive ? 'bg-secondary-500/70' : 'bg-[#F87171]/60'
                              }`}
                              style={{ width: `${Math.max(longBarPct, 3)}%` }}
                            >
                              {longBarPct >= 8 && (
                                <span className="text-[11px] font-bold text-background-50 ml-2 whitespace-nowrap">
                                  {longIsPositive ? '+' : ''}{pair.long.toFixed(2)}
                                </span>
                              )}
                            </div>
                            {longBarPct < 8 && (
                              <span className="text-[11px] font-bold text-foreground-300 ml-2 whitespace-nowrap">
                                {longIsPositive ? '+' : ''}{pair.long.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-foreground-400 w-[48px] flex-shrink-0 text-right">
                            pips/day
                          </span>
                        </div>

                        {/* Short bar */}
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-foreground-400 w-[38px] flex-shrink-0 text-right">
                            Short
                          </span>
                          <div className="flex-1 h-7 bg-foreground-800/80 rounded-sm overflow-hidden flex items-center relative">
                            <div
                              className={`h-full flex items-center transition-all duration-500 ${
                                shortIsPositive ? 'bg-secondary-500/70' : 'bg-[#F87171]/60'
                              }`}
                              style={{ width: `${Math.max(shortBarPct, 3)}%` }}
                            >
                              {shortBarPct >= 8 && (
                                <span className="text-[11px] font-bold text-background-50 ml-2 whitespace-nowrap">
                                  {shortIsPositive ? '+' : ''}{pair.short.toFixed(2)}
                                </span>
                              )}
                            </div>
                            {shortBarPct < 8 && (
                              <span className="text-[11px] font-bold text-foreground-300 ml-2 whitespace-nowrap">
                                {shortIsPositive ? '+' : ''}{pair.short.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-foreground-400 w-[48px] flex-shrink-0 text-right">
                            pips/day
                          </span>
                        </div>
                      </div>

                      {/* Column 3: Weekly dollar cost */}
                      <div className="hidden md:flex flex-col items-end justify-center gap-1.5">
                        <div className="text-[13px] font-semibold text-foreground-400">
                          Weekly at 1 lot
                        </div>
                        <div className={`text-[22px] md:text-[26px] font-[800] leading-none tracking-tight tabular-nums ${costColor}`}>
                          ${Math.abs(dominantCost).toFixed(0)}
                        </div>
                        <div className="text-[11px] text-foreground-400">
                          {weeklyLongCost <= 0 && weeklyShortCost <= 0
                            ? 'both sides pay'
                            : weeklyLongCost >= 0 && weeklyShortCost >= 0
                            ? 'both sides earn'
                            : 'direction matters'}
                        </div>
                      </div>
                    </div>

                    {/* Expanded detail panel */}
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isExpanded
                          ? 'grid-rows-[1fr] opacity-100 mt-5 pt-5 border-t border-foreground-700/40'
                          : 'grid-rows-[0fr] opacity-0 mt-0 pt-0 border-t-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 pb-2">
                          {/* Calculation breakdown */}
                          <div className="md:col-span-2 space-y-3">
                            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">
                              Calculation breakdown
                            </span>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="p-3 rounded-lg bg-foreground-800/50">
                                <div className="text-[11px] font-semibold text-foreground-400 mb-1.5">Long position</div>
                                <div className="text-[13px] text-foreground-200 leading-relaxed">
                                  <span className="text-foreground-300">{pair.long.toFixed(2)} pips</span>
                                  <span className="text-foreground-500"> × </span>
                                  <span className="text-foreground-300">${pipVal} pip value</span>
                                  <span className="text-foreground-500"> × </span>
                                  <span className="text-foreground-300">7 days</span>
                                  <span className="text-foreground-500"> = </span>
                                  <span className={`font-bold ${weeklyLongCost >= 0 ? 'text-secondary-300' : 'text-[#F87171]'}`}>
                                    ${Math.abs(weeklyLongCost).toFixed(0)}
                                  </span>
                                </div>
                              </div>
                              <div className="p-3 rounded-lg bg-foreground-800/50">
                                <div className="text-[11px] font-semibold text-foreground-400 mb-1.5">Short position</div>
                                <div className="text-[13px] text-foreground-200 leading-relaxed">
                                  <span className="text-foreground-300">{pair.short.toFixed(2)} pips</span>
                                  <span className="text-foreground-500"> × </span>
                                  <span className="text-foreground-300">${pipVal} pip value</span>
                                  <span className="text-foreground-500"> × </span>
                                  <span className="text-foreground-300">7 days</span>
                                  <span className="text-foreground-500"> = </span>
                                  <span className={`font-bold ${weeklyShortCost >= 0 ? 'text-secondary-300' : 'text-[#F87171]'}`}>
                                    ${Math.abs(weeklyShortCost).toFixed(0)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Quick insight */}
                          <div className="flex flex-col justify-center p-3 rounded-lg bg-foreground-800/40">
                            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400 mb-2">
                              Key takeaway
                            </span>
                            <p className="text-[13px] leading-relaxed text-foreground-300">
                              {isExtreme
                                ? `${pair.pair} swap dwarfs spread & commission. Holding overnight without checking swap is gambling.`
                                : `${pair.pair} swap is manageable for swing trades, but over weeks it adds up — always include it in your trade plan.`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty state */}
            {filteredPairs.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-foreground-400 text-[15px]">No pairs match this filter.</p>
                <button
                  onClick={() => setActiveFilter('All')}
                  className="mt-3 text-[13px] font-semibold text-secondary-300 hover:text-secondary-200 cursor-pointer transition-colors"
                >
                  Show all pairs
                </button>
              </div>
            )}

            {/* Bottom insight */}
            <div className="mt-8 pt-6 border-t border-foreground-700/40">
              <div className="flex items-start gap-3">
                <div className="w-1 h-full min-h-[32px] rounded-full bg-accent-400/40 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[14px] leading-relaxed text-foreground-200 font-semibold mb-1">
                    The pair matters more than the direction.
                  </p>
                  <p className="text-[13px] leading-relaxed text-foreground-400">
                    A 1-lot EURUSD held for a week costs about $3 in swap. A 1-lot USDTRY in the same period can cost over $200. That is a 65× difference — and it has nothing to do with your trading skill. Check swap rates before you hold.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-4 text-[12px] text-foreground-500 text-center">
          Swap rates shown are indicative. Actual rates vary by broker and update daily based on interbank tom-next rates.
        </p>
      </div>
    </section>
  );
}